/**
 * Partición de boleta interna en dos tramos (Consideraciones Previas).
 * Fechas de Interna 1 se eligen; el resto del período va a Interna 2.
 * Faltas/eventos siguen la fecha del tareo.
 */

import { normalizarFechaTareo } from './tareoEventoEfectivo';

export function mapaTramosDesdeApi(data) {
  if (data?.mapa && typeof data.mapa === 'object') {
    const out = {};
    Object.entries(data.mapa).forEach(([empId, row]) => {
      out[String(empId)] = normalizarTramo(row);
    });
    return out;
  }
  const out = {};
  (data?.tramos || []).forEach((row) => {
    if (row?.empleado_id == null) return;
    out[String(row.empleado_id)] = normalizarTramo(row);
  });
  return out;
}

export function normalizarTramo(row) {
  if (!row) return null;
  const fechas = Array.isArray(row.fechas_tramo_1)
    ? row.fechas_tramo_1.map((f) => normalizarFechaTareo(f)).filter(Boolean)
    : [];
  return {
    empleado_id: Number(row.empleado_id),
    activo: Boolean(row.activo),
    fechas_tramo_1: [...new Set(fechas)].sort(),
    sueldo_interno_1: row.sueldo_interno_1 != null ? Number(row.sueldo_interno_1) : null,
    sueldo_interno_2: row.sueldo_interno_2 != null ? Number(row.sueldo_interno_2) : null,
    destino_descuentos_plame: row.destino_descuentos_plame || 'interna_1',
  };
}

export function tramoActivoParaEmpleado(mapa, empleadoId) {
  const row = mapa?.[String(empleadoId)] || mapa?.[Number(empleadoId)];
  if (!row || !row.activo) return null;
  if (!Array.isArray(row.fechas_tramo_1) || row.fechas_tramo_1.length === 0) return null;
  if (row.sueldo_interno_1 == null || row.sueldo_interno_2 == null) return null;
  return row;
}

/**
 * Parte tareos: set de fechas_tramo_1 → tramo 1; resto → tramo 2.
 */
export function partirTareosPorTramos(tareos, fechasTramo1) {
  const set1 = new Set((fechasTramo1 || []).map((f) => normalizarFechaTareo(f)).filter(Boolean));
  const tareos1 = [];
  const tareos2 = [];
  (tareos || []).forEach((t) => {
    const f = normalizarFechaTareo(t.fecha);
    if (f && set1.has(f)) tareos1.push(t);
    else tareos2.push(t);
  });
  return { tareos1, tareos2 };
}

/**
 * Conceptos de tareo (ingresos/descuentos) solo de las fechas del tramo.
 * Conserva conceptos de plantilla (sin origen tareo) según `incluirPlantilla`.
 */
export function filtrarConceptosUsuarioPorTareos(lista, tareosTramo, { incluirPlantilla = false } = {}) {
  const setFechas = new Set(
    (tareosTramo || []).map((t) => normalizarFechaTareo(t.fecha)).filter(Boolean)
  );

  // Reconstruir montos de tareo solo de este tramo
  const porCodigo = new Map();
  (tareosTramo || []).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (String(c.tipo_boleta || 'plame').toLowerCase() !== 'interna') return;
      const tipo = String(c.tipo_concepto || '');
      if (!['ingreso', 'descuento', 'adelanto'].includes(tipo)) return;
      const codigo =
        c.plame_configuracion?.codigo_plame
        || c.codigo_plame
        || c.codigo
        || '';
      const monto = parseFloat(c.monto) || 0;
      if (!codigo || monto <= 0) return;
      const prev = porCodigo.get(codigo) || {
        codigo,
        monto: 0,
        tipo_concepto: tipo === 'adelanto' ? 'descuento' : tipo,
        descripcion: c.plame_configuracion?.descripcion || c.descripcion || codigo,
        origenTareo: true,
        origen_tareo: true,
        origenCalculo: 'tareo',
        montoTareo: 0,
      };
      prev.monto += monto;
      prev.montoTareo += monto;
      porCodigo.set(codigo, prev);
    });
  });

  const resultado = [];

  (lista || []).forEach((c) => {
    const cod = String(
      c.codigoPlame || c.codigo_plame || c.codigo || ''
    );
    const origen = String(c.origenCalculo || c.origen_calculo || c.origen || '').toLowerCase();
    const esTareo = Boolean(
      c.origenTareo || c.origen_tareo || c.desdeTareo || origen === 'tareo' || origen.includes('viatico')
    );
    if (esTareo) {
      // se reemplazan abajo desde porCodigo
      return;
    }
    if (incluirPlantilla) {
      resultado.push({ ...c });
    }
  });

  porCodigo.forEach((item) => {
    resultado.push({
      ...item,
      codigo_plame: item.codigo,
      codigoPlame: item.codigo,
      monto: parseFloat(item.monto.toFixed(2)),
      montoTareo: parseFloat(item.montoTareo.toFixed(2)),
    });
  });

  return resultado;
}

/** Fusiona dos resultados de boleta interna (suma montos por código). */
export function mergeResultadosBoletaInterna(r1, r2) {
  if (!r1?.success && !r2?.success) return null;
  if (!r1?.success) return r2;
  if (!r2?.success) return r1;

  const mergeLista = (a = [], b = []) => {
    const map = new Map();
    const keyOf = (item) => String(
      item?.id_concepto?.codigo
      || item?.id_concepto?.codigo_plame
      || item?.codigo
      || item?.codigo_plame
      || ''
    );
    [...a, ...b].forEach((item) => {
      const k = keyOf(item);
      if (!k) return;
      if (!map.has(k)) {
        map.set(k, { ...item, valor: Number(item.valor) || 0 });
        return;
      }
      const prev = map.get(k);
      prev.valor = parseFloat(((Number(prev.valor) || 0) + (Number(item.valor) || 0)).toFixed(2));
    });
    return Array.from(map.values());
  };

  const d1 = r1.data || {};
  const d2 = r2.data || {};
  const dataIngresos = mergeLista(d1.dataIngresos, d2.dataIngresos);
  const dataDescuentos = mergeLista(d1.dataDescuentos, d2.dataDescuentos);
  const dataAportes = mergeLista(d1.dataAportes, d2.dataAportes);
  const ti = dataIngresos.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const td = dataDescuentos.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const ta = dataAportes.reduce((s, i) => s + (Number(i.valor) || 0), 0);

  return {
    success: true,
    data: {
      ...d1,
      dataIngresos,
      dataDescuentos,
      dataAportes,
      totales: {
        totalIngresos: parseFloat(ti.toFixed(2)),
        totalDescuentos: parseFloat(td.toFixed(2)),
        totalAportes: parseFloat(ta.toFixed(2)),
        netoAPagar: parseFloat((ti - td).toFixed(2)),
      },
      _tramos: true,
    },
  };
}

function moneyPlain(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return `S/ ${v.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Texto de aclaración para una sola boleta interna con sueldo partido.
 * Ej: "Sueldo partido: S/ 1,130.00 (7 días) y S/ 800.00 (5 días)."
 */
export function textoAclaracionSueldoPartido({
  dias1,
  dias2,
  sueldo1,
  sueldo2,
  formatCurrency = moneyPlain,
} = {}) {
  const partes = [];
  if (sueldo1 != null && Number.isFinite(Number(sueldo1))) {
    const diasTxt = dias1 != null ? ` (${dias1} día${Number(dias1) === 1 ? '' : 's'})` : '';
    partes.push(`${formatCurrency(Number(sueldo1))}${diasTxt}`);
  }
  if (sueldo2 != null && Number.isFinite(Number(sueldo2))) {
    const diasTxt = dias2 != null ? ` (${dias2} día${Number(dias2) === 1 ? '' : 's'})` : '';
    partes.push(`${formatCurrency(Number(sueldo2))}${diasTxt}`);
  }
  if (partes.length < 2) return '';
  return `Sueldo partido: ${partes.join(' y ')}.`;
}

export function esConceptoRemuneracionBasica(concepto) {
  const codigo = String(concepto?.codigoPlame || concepto?.codigo || concepto?.codigo_plame || '').trim();
  if (codigo === '0121') return true;
  const desc = String(concepto?.descripcion || concepto?.nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return desc.includes('remuneracion') && (desc.includes('basico') || desc.includes('jornal'));
}

/**
 * Ej: "Sueldo 1: S/ 1,700.00 (15 días)"
 */
export function textoSueldoTramoInterna({
  numero = 1,
  sueldo,
  dias,
  formatCurrency = moneyPlain,
} = {}) {
  if (sueldo == null || !Number.isFinite(Number(sueldo))) return '';
  const n = Number(numero) === 2 ? 2 : 1;
  const diasTxt = dias != null && Number.isFinite(Number(dias))
    ? ` (${Number(dias)} día${Number(dias) === 1 ? '' : 's'})`
    : '';
  return `Sueldo ${n}: ${formatCurrency(Number(sueldo))}${diasTxt}`;
}

export function anotarConceptosConSueldoTramo(grupo, opts) {
  if (!grupo || typeof grupo !== 'object') return grupo;
  const etiqueta = textoSueldoTramoInterna(opts);
  if (!etiqueta) return grupo;
  return {
    ...grupo,
    ingresos: (grupo.ingresos || []).map((c) => (
      esConceptoRemuneracionBasica(c)
        ? { ...c, detalleSueldoTramo: etiqueta }
        : c
    )),
  };
}

export default {
  mapaTramosDesdeApi,
  normalizarTramo,
  tramoActivoParaEmpleado,
  partirTareosPorTramos,
  filtrarConceptosUsuarioPorTareos,
  mergeResultadosBoletaInterna,
  textoAclaracionSueldoPartido,
  textoSueldoTramoInterna,
  esConceptoRemuneracionBasica,
  anotarConceptosConSueldoTramo,
};
